import Box from '@mui/material/Box';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DataContext } from '../../aws/DataProvider';
import { useTranslation } from 'react-i18next';
import { CustomDataGrid } from '../data-grid/CustomDataGrid';
import { Utils, Customers } from '@hellohair/types';
import { GridRowParams } from '@mui/x-data-grid';
import { CustomModal } from '../modal/CustomModal';
import { UserRequestHistory } from '../modals/user-request-history/UserRequestHistory';
import { useStyles } from '../../theme/styles.helpers';
import { CustomersStyles } from './Customers.Styles';

interface CustomersListRow {
  customerId: string;
  email: string;
  fullName: string;
  assignedDoctor: string;
}

export interface ShopifyOrder {
  createdAt: string;
  name: string;
  lineItems: {
    title: string;
    id: string;
    price: string;
    sellingPlanName?: string;
  }[];
}

export interface ShopifyCustomerWithOrders {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  orders: ShopifyOrder[];
}

export interface AppstleSubscription {
  id: number;
  billingPolicyInterval: string;
  billingPolicyIntervalCount: number;
  customerId: number; // Long in GraphQL
  status: string;
  orderId: number; // Long in GraphQL
  createdAt: string;
  updatedAt: string;
  nextBillingDate: string;
  orderAmount: number;
  orderName: string;
  customerName: string;
  customerEmail: string;
  activatedOn: string;
  pausedOn: string | null;
  cancelledOn: string | null;
  totalSuccessfulOrders: number;
  contractDetailsJSON: string;
}

type Props = {
  search: string;
  customers: (Customers.ShopCustomer & {shopifyOrders?: ShopifyOrder[]})[];
  fromShopify: boolean;
  appstleSubscriptions?: AppstleSubscription[];
};

const CustomersList = ({ search, customers, fromShopify, appstleSubscriptions }: Props) => {
  const styles = useStyles(CustomersStyles, {});
  const { t } = useTranslation();
  const [rows, setRows] = useState<CustomersListRow[]>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customerModal, setCustomerModal] = useState<Customers.ShopCustomer & {shopifyOrders?: ShopifyOrder[]; appstleSubscriptions?: AppstleSubscription[]} | null>(null);

  const {
    usersState: [users],
    studiosState: [studios],
  } = useContext(DataContext);
  const doctors = users.filter((user) => user.groups.includes('doctor'));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const transformToRow: (request: Utils.DBItem<any>) => CustomersListRow = (request: Utils.DBItem<any>) => {
    const assignedDoctor = doctors.find((doctor) => doctor.sub === request.doctorId);
    const studioSource = studios.find((studio) => studio.id === request.studioSource);

    return {
      customerId: request.customerId,
      email: request.email,
      fullName: request.firstName + ' ' + request.lastName,
      assignedDoctor: assignedDoctor ? assignedDoctor.firstName + ' ' + assignedDoctor.lastName : '',
      studioSource: studioSource?.name || '',
      dateCreated: new Date(request.dateCreated),
    };
  };

  useEffect(() => {
    setRows(customers.map(transformToRow));
  }, [customers]);

  useEffect(() => {
    const _rows = customers.map(transformToRow).filter((row) => {
      return Object.values(row).some((value) => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(search.toLowerCase());
        }
        return false;
      });
    });
    setRows(_rows);
  }, [search]);

  // TODO: Data from AWS
  const columns = useMemo(
    () => [
      { field: 'email', headerName: t('E-Mail'), filterable: false, flex: 1 },
      { field: 'fullName', headerName: t('Name'), filterable: false, flex: 1 },
      { field: 'assignedDoctor', headerName: t('Doctor'), filterable: false, flex: 1 },
      { field: 'studioSource', headerName: t('Source'), filterable: false, flex: 1 },
      { field: 'dateCreated', headerName: t('Date created'), type: 'date', filterable: false, flex: 1 },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleRowClicked = (params: GridRowParams) => {
    const selectedCustomer = customers.find((row) => row.customerId === params.row.customerId);

    if (selectedCustomer) {
      const customerAppstleSubscriptions = appstleSubscriptions?.filter((sub) => sub.customerId.toString() === selectedCustomer.customerId) || [];
      setCustomerModal({
        ...selectedCustomer,
        appstleSubscriptions: customerAppstleSubscriptions,
      });
      setIsModalOpen(true);
    }
  };

  return (
    <Box sx={styles.root} className="Patients-root">
      <CustomDataGrid
        columns={columns}
        rows={rows || []}
        onRowClick={handleRowClicked}
        getRowId={(row) => row.email}
        isCellEditable={() => false}
        isRowSelectable={() => false}
        pagination
        autoPageSize
        sx={styles.row}
        ignoreDiacritics
        initialState={{
          sorting: {
            sortModel: [{ field: 'dateCreated', sort: 'desc' }],
          },
        }}
      />
      <CustomModal variant="large" isModalOpen={isModalOpen} onModalClose={() => setIsModalOpen(false)}>
        <UserRequestHistory
          onModalClose={() => setIsModalOpen(false)}
          customerId={fromShopify ? customerModal?.email || '' : customerModal?.customerId || ''}
          firstName={customerModal?.firstName || ''}
          lastName={customerModal?.lastName || ''}
          stripeId={customerModal?.stripeId || ''}
          customerEmail={fromShopify ? customerModal?.email || '' : undefined}
          shopifyOrders={customerModal?.shopifyOrders}
          appstleSubscriptions={customerModal?.appstleSubscriptions}
        />
      </CustomModal>
    </Box>
  );
};

export default CustomersList;
